import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { DialogOpenEvent } from '@theme/dialog';

/**
 * Cart-drawer shipment scheduler.
 *
 * Renders the footer's delivery line ("Ships today · arrives Oct 1–5"). Orders
 * ship on the earliest shippable date unless the customer taps "Ship later" and
 * picks a date from the calendar sheet; the link then reads "Change". Picking the
 * earliest date again goes back to shipping now. Persists the choice to
 * `cart.attributes` so it surfaces at checkout and in the order admin.
 *
 * Configuration is read from data-* attributes set by the snippet from
 * theme settings (lead times, cutoff, ship days, blackout dates, etc.).
 *
 * A cart holding nothing but pre-sell items (`data-presell-iso`) is locked to
 * that release date: the "Ship later" link is hidden and the date can't be changed. A cart
 * that mixes in anything shippable now schedules as usual — the pre-sell lines
 * carry their own `Will Ship` property.
 */
class ShipmentScheduler extends Component {
  /** @type {AbortController | null} */
  #activeFetch = null;

  /** @type {Element | null} */
  #drawer = null;

  /** @type {MutationObserver | null} */
  #presellObserver = null;

  /** @type {MutationObserver | null} */
  #lineObserver = null;

  connectedCallback() {
    super.connectedCallback();

    this.config = this.#parseConfig();
    this.state = {
      mode: 'now',
      committed: null,
      draft: null,
      viewMonth: null,
      sheetOpen: false,
      locked: false,
    };

    this.earliest = this.#computeEarliestShipDate();

    const presellDate = this.#presellDate();
    const savedIso = (this.dataset.savedIso || '').trim();
    // A saved date the lock put there, on a cart that has since gained something shippable
    // now (on another page), was never the shopper's choice — go back to Ship now.
    const savedByLock = this.dataset.savedPresellLock === 'true';
    if (presellDate) {
      this.#lockTo(presellDate);
    } else if (savedIso && !savedByLock) {
      const saved = this.#parseIso(savedIso);
      if (saved && saved > this.earliest) {
        this.state.mode = 'later';
        this.state.committed = saved;
      } else {
        this.state.committed = this.earliest;
      }
    } else {
      this.state.committed = this.earliest;
    }
    this.state.draft = this.state.committed;
    this.state.viewMonth = startOfMonth(this.state.committed);

    document.addEventListener('keyup', this.#onKeyUp);

    // The cart drawer lives in the header section, which re-renders itself twice over:
    // once when the header hydrates after load, and again by morphing fresh HTML in on
    // every cart update. Both keep this element — so connectedCallback doesn't run
    // again — while resetting the delivery line to the empty placeholder it ships with.
    // The dates are still in `state`, just no longer on screen.
    //
    // Repainting when the drawer opens is what actually matters: that is the only
    // moment the readout is visible, and it covers a reset from either source.
    document.addEventListener(ThemeEvents.cartUpdate, this.#repaint);
    this.#drawer = this.closest('cart-drawer-component');
    this.#drawer?.addEventListener(DialogOpenEvent.eventName, this.#repaint);

    // The morph that brings a cart update's markup in lands after the cart:update event,
    // and often after the frame #repaint waits for — so the pre-sell lock follows the
    // attribute itself, which the morph rewrites in place. The lock and the attributes it
    // saves are settled straight away rather than on a frame: ShipStation reads them, and a
    // frame never comes while the tab is in the background.
    this.#presellObserver = new MutationObserver(this.#onPresellChange);
    this.#presellObserver.observe(this, { attributes: true, attributeFilter: ['data-presell-iso'] });

    // Repainting on open and on cart:update isn't enough on its own: a re-render can land
    // after both, leaving the delivery line blank until something repaints it. So whenever
    // a re-render leaves the line blank, paint it again.
    this.#lineObserver = new MutationObserver(this.#onLineChange);
    this.#lineObserver.observe(this, { childList: true, characterData: true, subtree: true });

    this.#renderReadout();
    this.#persist({ silent: true });
  }

  /**
   * Repaint on the next frame rather than inline: a cart update re-renders the drawer
   * from the same event, and painting first would just be overwritten by the morph.
   */
  #repaint = () => {
    requestAnimationFrame(() => {
      this.#syncPresellLock();
      this.#renderReadout();
    });
  };

  // Checks the whole subtree rather than the line alone, since a re-render can swap the
  // line's node out entirely. Our own paint triggers this too, but it leaves the line
  // filled in, so it doesn't loop.
  #onLineChange = () => {
    const line = this.#deliveryLine();
    if (line && !line.textContent?.trim()) this.#renderReadout();
  };

  #onPresellChange = () => {
    this.#syncPresellLock();
    this.#repaint();
  };

  /**
   * The pre-sell release date the whole cart ships on, or null when anything in it
   * can ship sooner. Read fresh each time: the morph updates the attribute in place.
   * @returns {Date | null}
   */
  #presellDate() {
    return this.#parseIso((this.dataset.presellIso || '').trim());
  }

  /** @param {Date} date */
  #lockTo(date) {
    this.state.locked = true;
    this.state.mode = 'later';
    this.state.committed = date;
    this.state.draft = date;
    this.state.viewMonth = startOfMonth(date);
    if (this.state.sheetOpen) this.closeSheet();
  }

  /**
   * A cart update can turn the lock on (the last ship-now item was removed) or off
   * (something shippable now was added). Either way the saved ship date is no longer
   * right, so persist the new one. Unlocking goes back to Ship now, since the pre-sell
   * date was never the shopper's choice.
   */
  #syncPresellLock() {
    const presellDate = this.#presellDate();

    if (presellDate) {
      const unchanged = this.state.locked && this.#formatIso(presellDate) === this.#formatIso(this.state.committed);
      if (unchanged) return;
      this.#lockTo(presellDate);
      this.#persist();
    } else if (this.state.locked) {
      this.state.locked = false;
      this.state.mode = 'now';
      this.state.committed = this.earliest;
      this.state.draft = this.earliest;
      this.state.viewMonth = startOfMonth(this.earliest);
      this.#persist();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('keyup', this.#onKeyUp);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#repaint);
    this.#drawer?.removeEventListener(DialogOpenEvent.eventName, this.#repaint);
    this.#drawer = null;
    this.#presellObserver?.disconnect();
    this.#presellObserver = null;
    this.#lineObserver?.disconnect();
    this.#lineObserver = null;
    if (this.#activeFetch) this.#activeFetch.abort();
  }

  #parseConfig() {
    const ds = this.dataset;
    const shipDays = (ds.shipDays || '').split(',').map((s) => s.trim() === 'true');
    let blackouts = [];
    try {
      blackouts = JSON.parse(ds.blackouts || '[]');
    } catch (e) {
      blackouts = [];
    }
    const shopNowStr = (ds.shopNow || '').replace(' ', 'T');
    const shopNow = shopNowStr ? new Date(shopNowStr) : new Date();
    return {
      leadMin: parseInt(ds.leadMin, 10) || 0,
      leadMax: parseInt(ds.leadMax, 10) || 0,
      cutoff: ds.cutoff || '14:00',
      maxFuture: parseInt(ds.maxFuture, 10) || 60,
      shopNow,
      shipDays,
      blackouts: new Set(blackouts),
    };
  }

  #computeEarliestShipDate() {
    const now = this.config.shopNow;
    const [hh, mm] = (this.config.cutoff || '14:00').split(':').map((n) => parseInt(n, 10));
    const cutoffMinutes = (hh || 0) * 60 + (mm || 0);
    const nowMinutes = now.getHours() * 60 + now.getMinutes();
    const candidate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    if (nowMinutes >= cutoffMinutes) candidate.setDate(candidate.getDate() + 1);
    let guard = 0;
    while (!this.#isShippable(candidate) && guard < 365) {
      candidate.setDate(candidate.getDate() + 1);
      guard++;
    }
    return candidate;
  }

  #isShippable(date) {
    if (!this.config.shipDays[date.getDay()]) return false;
    if (this.config.blackouts.has(this.#formatIso(date))) return false;
    return true;
  }

  #maxSelectableDate() {
    const d = new Date(this.earliest);
    d.setDate(d.getDate() + this.config.maxFuture);
    return d;
  }

  #onKeyUp = (event) => {
    if (event.key === 'Escape' && this.state.sheetOpen) this.closeSheet();
  };

  // --- Public methods (referenced by `on:` attributes in the snippet) ---

  selectShipLater() {
    if (this.state.locked) return;
    this.state.draft = this.state.committed > this.earliest ? this.state.committed : this.earliest;
    this.state.viewMonth = startOfMonth(this.state.draft);
    this.openSheet();
  }

  openSheet() {
    this.state.sheetOpen = true;
    this.refs.sheet?.setAttribute('aria-hidden', 'false');
    this.refs.scrim?.setAttribute('aria-hidden', 'false');
    this.refs.sheet?.classList.add('is-open');
    this.refs.scrim?.classList.add('is-open');
    this.#renderCalendar();
    this.#renderDraftSummary();
    this.refs.sheet?.querySelector('.shipment-scheduler__sheet-close')?.focus();
  }

  closeSheet() {
    const hadFocus = this.refs.sheet?.contains(document.activeElement);
    this.state.sheetOpen = false;
    this.refs.sheet?.setAttribute('aria-hidden', 'true');
    this.refs.scrim?.setAttribute('aria-hidden', 'true');
    this.refs.sheet?.classList.remove('is-open');
    this.refs.scrim?.classList.remove('is-open');
    if (hadFocus) this.#shipLaterLink()?.focus();
  }

  prevMonth(event) {
    event?.stopPropagation();
    this.#shiftViewMonth(-1);
  }

  nextMonth(event) {
    event?.stopPropagation();
    this.#shiftViewMonth(1);
  }

  #shiftViewMonth(offset) {
    const next = new Date(this.state.viewMonth);
    next.setMonth(next.getMonth() + offset);
    if (next < startOfMonth(this.earliest)) return;
    const max = this.#maxSelectableDate();
    if (next > startOfMonth(max)) return;
    this.state.viewMonth = next;
    this.#renderCalendar();
  }

  selectDate(event) {
    const target = event.target.closest('[data-iso]');
    if (!target || target.disabled) return;
    // Stop the click from bubbling to the cart-drawer dialog's click-outside
    // handler. Otherwise the calendar re-render removes the clicked button
    // from the DOM before that handler runs, and `dialog.contains(target)`
    // returns false → the drawer closes.
    event.stopPropagation();
    const iso = target.dataset.iso;
    const date = this.#parseIso(iso);
    if (!date) return;
    this.state.draft = date;
    this.#renderCalendar();
    this.#renderDraftSummary();
  }

  confirmShipDate() {
    if (this.state.locked) return;
    // With the Ship now toggle gone, picking the earliest date is how a shopper goes back.
    const isEarliest = this.#formatIso(this.state.draft) === this.#formatIso(this.earliest);
    this.state.mode = isEarliest ? 'now' : 'later';
    this.state.committed = isEarliest ? this.earliest : this.state.draft;
    this.closeSheet();
    this.#renderReadout();
    this.#persist();
  }

  // --- Render helpers ---

  // Use direct querySelector instead of this.refs to avoid any race between
  // connectedCallback and the Component base class's ref population (the
  // mutation observer is set up inside requestIdleCallback, and we want the
  // first render to be 100% deterministic regardless of ref-map readiness).
  // These two are queried fresh: a re-render can swap the nodes out before refs catch up.
  #deliveryLine() { return this.querySelector('[ref="deliveryLine"]'); }
  #shipLaterLink() { return this.querySelector('[ref="shipLaterLink"]'); }
  #draftShip() { return this.refs.draftShip ?? this.querySelector('[ref="draftShip"]'); }
  #draftDelivery() { return this.refs.draftDelivery ?? this.querySelector('[ref="draftDelivery"]'); }

  #renderReadout() {
    const ship = this.state.committed;
    const line = this.#deliveryLine();
    const link = this.#shipLaterLink();

    if (link) {
      link.hidden = this.state.locked;
      link.textContent = this.state.mode === 'later' ? 'Change' : 'Ship later';
    }

    if (!line) return;
    if (!ship) {
      line.textContent = '—';
      return;
    }
    const shipLabel = this.#formatIso(ship) === this.#formatIso(this.config.shopNow) ? 'today' : formatShort(ship);
    const range = this.#computeDeliveryRange(ship);
    line.innerHTML = `Ships ${shipLabel} · <b>arrives ${formatRangeCompact(range.start, range.end)}</b>`;
  }

  #renderDraftSummary() {
    const ship = this.state.draft;
    const draftShip = this.#draftShip();
    const draftDelivery = this.#draftDelivery();
    if (!ship) {
      if (draftShip) draftShip.textContent = '—';
      if (draftDelivery) draftDelivery.textContent = '—';
      return;
    }
    if (draftShip) draftShip.textContent = formatShort(ship);
    const range = this.#computeDeliveryRange(ship);
    if (draftDelivery) draftDelivery.textContent = formatRange(range.start, range.end);
  }

  #computeDeliveryRange(shipDate) {
    const start = new Date(shipDate);
    start.setDate(start.getDate() + this.config.leadMin);
    const end = new Date(shipDate);
    end.setDate(end.getDate() + this.config.leadMax);
    return { start, end };
  }

  #renderCalendar() {
    const view = this.state.viewMonth;
    const grid = this.refs.grid;
    const monthLabel = this.refs.monthLabel;
    if (!grid || !monthLabel) return;

    monthLabel.textContent = view.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

    const startWeekday = new Date(view.getFullYear(), view.getMonth(), 1).getDay();
    const daysInMonth = new Date(view.getFullYear(), view.getMonth() + 1, 0).getDate();
    const max = this.#maxSelectableDate();
    const draftIso = this.state.draft ? this.#formatIso(this.state.draft) : '';
    const range = this.state.draft ? this.#computeDeliveryRange(this.state.draft) : null;

    const cells = [
      '<div class="shipment-scheduler__cal-dow">SU</div>',
      '<div class="shipment-scheduler__cal-dow">MO</div>',
      '<div class="shipment-scheduler__cal-dow">TU</div>',
      '<div class="shipment-scheduler__cal-dow">WE</div>',
      '<div class="shipment-scheduler__cal-dow">TH</div>',
      '<div class="shipment-scheduler__cal-dow">FR</div>',
      '<div class="shipment-scheduler__cal-dow">SA</div>',
    ];
    for (let i = 0; i < startWeekday; i++) {
      cells.push('<span class="shipment-scheduler__cal-cell shipment-scheduler__cal-cell--empty"></span>');
    }
    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(view.getFullYear(), view.getMonth(), d);
      const iso = this.#formatIso(date);
      const disabled = date < this.earliest || date > max || !this.#isShippable(date);
      const selected = iso === draftIso;
      const inRange = range && !selected && date >= range.start && date <= range.end;
      const classes = ['shipment-scheduler__cal-cell'];
      if (disabled) classes.push('is-dim');
      if (selected) classes.push('is-sel');
      if (inRange) classes.push('is-in-range');
      const ariaLabel = selected
        ? `Ship ${formatLong(date)}`
        : inRange
          ? `Estimated delivery ${formatLong(date)}`
          : formatLong(date);
      cells.push(
        `<button type="button" class="${classes.join(' ')}" data-iso="${iso}" on:click="/selectDate"${
          disabled ? ' disabled' : ''
        } aria-label="${ariaLabel}"${selected ? ' aria-pressed="true"' : ''}>${d}</button>`
      );
    }
    grid.innerHTML = cells.join('');
  }

  // --- Date utilities ---

  #formatIso(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  #parseIso(iso) {
    if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null;
    const [y, m, d] = iso.split('-').map((n) => parseInt(n, 10));
    return new Date(y, m - 1, d);
  }

  // --- Persistence ---

  async #persist({ silent = false } = {}) {
    const ship = this.state.committed;
    if (!ship) return;
    const range = this.#computeDeliveryRange(ship);
    const attributes = {
      'Ship Date': formatLong(ship),
      'Estimated Delivery': formatRange(range.start, range.end),
      _ship_date_iso: this.#formatIso(ship),
      _ship_mode: this.state.mode,
      // Marks the date as the pre-sell lock's rather than the shopper's. An empty value
      // removes the attribute, so ordinary orders don't carry it.
      _ship_presell_lock: this.state.locked ? 'true' : '',
    };

    if (this.#activeFetch) this.#activeFetch.abort();
    const abortController = new AbortController();
    this.#activeFetch = abortController;

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({ attributes }),
      });
      await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });
    } catch (err) {
      if (!silent && err.name !== 'AbortError') {
        console.error('Shipment scheduler persist failed:', err);
      }
    } finally {
      this.#activeFetch = null;
    }
  }
}

function startOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function formatLong(d) {
  return d.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatShort(d) {
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

/** "Oct 1–5", or "Sep 30–Oct 4" across months. Display only; orders get formatRange. */
function formatRangeCompact(start, end) {
  const month = (d) => d.toLocaleDateString('en-US', { month: 'short' });
  if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
    return `${month(start)} ${start.getDate()}–${end.getDate()}`;
  }
  return `${month(start)} ${start.getDate()}–${month(end)} ${end.getDate()}`;
}

function formatRange(start, end) {
  const sameMonth = start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear();
  if (sameMonth) {
    const month = start.toLocaleDateString('en-US', { month: 'short' });
    return `${month} ${start.getDate()} – ${end.getDate()}`;
  }
  const startStr = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const endStr = end.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return `${startStr} – ${endStr}`;
}

if (!customElements.get('shipment-scheduler-component')) {
  customElements.define('shipment-scheduler-component', ShipmentScheduler);
}
